'use client'

import { useId, useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import posthog from 'posthog-js';
import { useForm } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { FadeIn } from '@/components/FadeIn'
import ContactButton from '@/components/ContactButton'
import AlertMessage from '@/components/AlertMessage'

const contactFormSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
        "@type": "WebForm",
        "name": "Business Inquiry Contact Form",
        "description": "Contact Kingdom Code for web development, app development, and digital transformation services",
        "url": typeof window !== 'undefined' ? window.location.href : '',
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": "/api/lead",
            "httpMethod": "POST",
            "contentType": "application/json"
        }
    }
};

const STEPS = [
    { id: 'about', label: 'About You', fields: ['name', 'email', 'phone'] },
    { id: 'project', label: 'Your Project', fields: ['company', 'message', 'budget', 'customBudget'] },
    { id: 'review', label: 'Review & Send', fields: [] },
];

function TextInput({ label, register, name, validationRules, errors, ...props }) {
    let id = useId()

    return (
        <div className="group relative z-0 transition-all focus-within:z-10">
            <input
                type="text"
                id={id}
                {...register(name, validationRules)}
                {...props}
                placeholder=" "
                aria-label={label}
                aria-invalid={errors[name] ? "true" : "false"}
                aria-describedby={errors[name] ? `${id}-error` : undefined}
                className="peer block w-full border border-neutral-300 bg-transparent px-6 pt-12 pb-4 text-base/6 text-neutral-950 ring-4 ring-transparent transition group-first:rounded-t-2xl group-last:rounded-b-2xl focus:border-neutral-950 focus:ring-neutral-950/5 focus:outline-hidden"
            />
            <label
                htmlFor={id}
                className="pointer-events-none absolute top-1/2 left-6 -mt-3 origin-left text-base/6 text-neutral-500 transition-all duration-200 peer-not-placeholder-shown:-translate-y-4 peer-not-placeholder-shown:scale-75 peer-not-placeholder-shown:font-semibold peer-not-placeholder-shown:text-neutral-950 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:font-semibold peer-focus:text-neutral-950"
            >
                {label}
            </label>
            {errors[name] && (
                <p id={`${id}-error`} className="mt-1 text-sm text-red-500 px-6" role="alert">{errors[name].message}</p>
            )}
        </div>
    )
}

function RadioInput({ label, register, name, value, ...props }) {
    return (
        <label className="flex gap-x-3 cursor-pointer">
            <input
                type="radio"
                value={value}
                {...register(name)}
                {...props}
                aria-label={`Budget option: ${label}`}
                className="h-6 w-6 flex-none appearance-none rounded-full border border-neutral-950/20 outline-hidden checked:border-[0.5rem] checked:border-neutral-950 focus-visible:ring-1 focus-visible:ring-neutral-950 focus-visible:ring-offset-2"
            />
            <span className="text-base/6 text-neutral-950">{label}</span>
        </label>
    )
}

function StepIndicator({ currentStep, steps }) {
    return (
        <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;

                return (
                    <div key={step.id} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                            <motion.div
                                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                                    isCompleted
                                        ? 'bg-neutral-950 text-white'
                                        : isActive
                                            ? 'border-2 border-neutral-950 text-neutral-950'
                                            : 'border-2 border-neutral-300 text-neutral-400'
                                }`}
                                animate={isActive ? { scale: [1, 1.1, 1] } : {}}
                                transition={{ duration: 0.3 }}
                            >
                                {isCompleted ? (
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    index + 1
                                )}
                            </motion.div>
                            <span className={`mt-1.5 text-xs font-medium whitespace-nowrap ${
                                isActive ? 'text-neutral-950' : isCompleted ? 'text-neutral-700' : 'text-neutral-400'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                        {index < steps.length - 1 && (
                            <div className="relative mx-3 h-0.5 flex-1 bg-neutral-200 self-start mt-4">
                                <motion.div
                                    className="absolute inset-y-0 left-0 bg-neutral-950"
                                    initial={{ width: '0%' }}
                                    animate={{ width: isCompleted ? '100%' : '0%' }}
                                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ReviewField({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex flex-col gap-0.5">
            <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">{label}</dt>
            <dd className="text-base text-neutral-950">{value}</dd>
        </div>
    );
}

const slideVariants = {
    enter: (direction) => ({
        x: direction > 0 ? 80 : -80,
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction) => ({
        x: direction > 0 ? -80 : 80,
        opacity: 0,
    }),
};

export default function ContactForm() {
    const recaptcha = useRef();
    const [formStatus, setFormStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [direction, setDirection] = useState(1);
    const [showCustomBudget, setShowCustomBudget] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        trigger,
        getValues,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            company: '',
            phone: '',
            message: '',
            budget: '$25K – $50K',
            customBudget: ''
        }
    });

    const selectedBudget = watch('budget');

    useEffect(() => {
        setShowCustomBudget(selectedBudget === 'Other');
        if (selectedBudget !== 'Other') {
            setValue('customBudget', '');
        }
    }, [selectedBudget, setValue]);

    const goToNext = useCallback(async () => {
        const fieldsToValidate = STEPS[currentStep].fields;
        const isValid = await trigger(fieldsToValidate);
        if (!isValid) return;

        setDirection(1);
        setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }, [currentStep, trigger]);

    const goToPrev = useCallback(() => {
        setDirection(-1);
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    }, []);

    const onSubmit = async (data) => {
        try {
            setFormStatus('sending');
            setErrorMessage('');

            if (data.budget === 'Other') {
                if (!data.customBudget) {
                    setErrorMessage('Please specify your budget');
                    setFormStatus('error');
                    return;
                }
                data.budget = data.customBudget;
            }

            const captchaValue = recaptcha.current.getValue()

            if (!captchaValue) {
                setErrorMessage('Please verify the reCAPTCHA!');
                setFormStatus('error');
                return;
            }

            const response = await fetch('/api/captcha/verify', {
                method: 'POST',
                body: JSON.stringify({ captchaValue }),
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            const verificationRes = await response.json();

            if (!verificationRes.success) {
                setErrorMessage('Failed to verify reCAPTCHA. Please try again.');
                setFormStatus('error');
                return;
            }

            const leadResponse = await fetch('/api/lead', {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            const leadData = await leadResponse.json();

            if (leadData.message === "Lead created successfully" || leadData.message === "Lead already exists, successfully added to user notes") {
                posthog.identify(data.email, {
                  name: data.name,
                  email: data.email,
                  company: data.company || undefined,
                });
                posthog.capture('contact_form_submitted', {
                  budget: data.budget,
                  has_company: Boolean(data.company),
                });
                setFormStatus('success');
                setTimeout(() => {
                    reset();
                    setFormStatus('idle');
                    setCurrentStep(0);
                }, 3000);
            } else {
                setErrorMessage(leadData.message || 'Failed to send message. Please try again.');
                setFormStatus('error');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setErrorMessage('An unexpected error occurred. Please try again.');
            setFormStatus('error');
        }
    };

    const clearError = () => {
        setErrorMessage('');
        setFormStatus('idle');
    };

    const values = getValues();
    const isLastStep = currentStep === STEPS.length - 1;

    return (
        <FadeIn className="lg:order-last">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(contactFormSchema) }}
            />

            <form
                onSubmit={handleSubmit(onSubmit)}
                aria-label="Business inquiry contact form"
                itemScope
                itemType="https://schema.org/ContactPage"
            >
                <h2 className="font-display text-base font-semibold text-neutral-950">
                    Work inquiries
                </h2>
                <p className="mt-2 text-sm text-neutral-600">
                    Get in touch with Kingdom Code for web development, mobile apps, and digital transformation services.
                </p>

                {errorMessage && (
                    <AlertMessage
                        message={errorMessage}
                        type="error"
                        onClose={clearError}
                    />
                )}

                <div className="mt-6">
                    <StepIndicator currentStep={currentStep} steps={STEPS} />

                    <div className="relative overflow-hidden min-h-[320px]">
                        <AnimatePresence mode="wait" custom={direction}>
                            {currentStep === 0 && (
                                <motion.div
                                    key="step-0"
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                >
                                    <p className="text-sm text-neutral-500 mb-4">
                                        Tell us a bit about yourself so we can get back to you.
                                    </p>
                                    <div className="isolate -space-y-px rounded-2xl bg-white/50">
                                        <TextInput
                                            label="Name"
                                            name="name"
                                            autoComplete="name"
                                            register={register}
                                            validationRules={{ required: 'Name is required' }}
                                            errors={errors}
                                        />
                                        <TextInput
                                            label="Email"
                                            type="email"
                                            name="email"
                                            autoComplete="email"
                                            register={register}
                                            validationRules={{
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: "Invalid email address"
                                                }
                                            }}
                                            errors={errors}
                                        />
                                        <TextInput
                                            label="Phone"
                                            type="tel"
                                            name="phone"
                                            autoComplete="tel"
                                            register={register}
                                            errors={errors}
                                        />
                                    </div>
                                </motion.div>
                            )}

                            {currentStep === 1 && (
                                <motion.div
                                    key="step-1"
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                >
                                    <p className="text-sm text-neutral-500 mb-4">
                                        Tell us about your project and how much you&apos;re looking to invest.
                                    </p>
                                    <div className="isolate -space-y-px rounded-2xl bg-white/50">
                                        <TextInput
                                            label="Company"
                                            name="company"
                                            autoComplete="organization"
                                            register={register}
                                            errors={errors}
                                        />
                                        <TextInput
                                            label="Message"
                                            name="message"
                                            autoComplete="off"
                                            register={register}
                                            errors={errors}
                                        />
                                        <div className="border border-neutral-300 px-6 py-8 first:rounded-t-2xl last:rounded-b-2xl">
                                            <fieldset>
                                                <legend className="text-base/6 text-neutral-500 font-medium">Investment Budget Range</legend>
                                                <p className="text-sm text-neutral-500 mt-1">How much are you willing to invest in your business?</p>
                                                <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2" role="radiogroup" aria-label="Budget selection">
                                                    <RadioInput label="$2K – $5K" name="budget" value="$2K – $5K" register={register} />
                                                    <RadioInput label="$10K – $25K" name="budget" value="$10K – $25K" register={register} />
                                                    <RadioInput label="$25K – $50K" name="budget" value="$25K – $50K" register={register} />
                                                    <RadioInput label="$50K – $100K" name="budget" value="$50K – $100K" register={register} />
                                                    <RadioInput label="$100K+" name="budget" value="$100K+" register={register} />
                                                    <RadioInput label="Other" name="budget" value="Other" register={register} />
                                                    <AnimatePresence mode="wait">
                                                        {showCustomBudget && (
                                                            <motion.div
                                                                className="col-span-full mt-4"
                                                                initial={{ opacity: 0, height: 0, overflow: 'hidden' }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    height: 'auto',
                                                                    transition: {
                                                                        opacity: { duration: 0.2 },
                                                                        height: { duration: 0.3 }
                                                                    }
                                                                }}
                                                                exit={{
                                                                    opacity: 0,
                                                                    height: 0,
                                                                    transition: {
                                                                        opacity: { duration: 0.1 },
                                                                        height: { duration: 0.2 }
                                                                    }
                                                                }}
                                                            >
                                                                <TextInput
                                                                    label="Please specify your investment amount"
                                                                    name="customBudget"
                                                                    autoComplete="off"
                                                                    inputMode="decimal"
                                                                    register={register}
                                                                    errors={errors}
                                                                    validationRules={{
                                                                        required: 'Please specify your investment amount'
                                                                    }}
                                                                />
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            </fieldset>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {currentStep === 2 && (
                                <motion.div
                                    key="step-2"
                                    custom={direction}
                                    variants={slideVariants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                >
                                    <p className="text-sm text-neutral-500 mb-4">
                                        Review your details and submit when ready.
                                    </p>
                                    <div className="rounded-2xl border border-neutral-200 bg-white/50 divide-y divide-neutral-200">
                                        <div className="px-6 py-5">
                                            <h3 className="text-sm font-semibold text-neutral-950 mb-3">About You</h3>
                                            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <ReviewField label="Name" value={values.name} />
                                                <ReviewField label="Email" value={values.email} />
                                                <ReviewField label="Phone" value={values.phone} />
                                            </dl>
                                        </div>
                                        <div className="px-6 py-5">
                                            <h3 className="text-sm font-semibold text-neutral-950 mb-3">Your Project</h3>
                                            <dl className="grid grid-cols-1 gap-3">
                                                <ReviewField label="Company" value={values.company} />
                                                <ReviewField label="Message" value={values.message} />
                                                <ReviewField
                                                    label="Budget"
                                                    value={values.budget === 'Other' ? values.customBudget : values.budget}
                                                />
                                            </dl>
                                        </div>
                                    </div>

                                    <p className="mt-6 text-sm text-neutral-500 px-2">
                                        By submitting the form I agree with the{' '}
                                        <a
                                            href="/privacy-policy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-neutral-900 hover:underline"
                                            aria-label="Read our Privacy Policy"
                                        >
                                            Privacy Policy
                                        </a>
                                        {' '}and consent to receive SMS updates from Kingdom Kode.
                                    </p>

                                    <div className="flex w-full mt-6 justify-center flex-col items-center gap-3">
                                        <div aria-label="reCAPTCHA verification">
                                            <ReCAPTCHA ref={recaptcha} sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY} />
                                        </div>

                                        <div className="flex w-full">
                                            <ContactButton
                                                type="submit"
                                                disabled={isSubmitting || formStatus === 'sending' || formStatus === 'success'}
                                                status={formStatus}
                                                aria-label="Submit contact form"
                                                id="contact-form-submit"
                                            />
                                        </div>
                                        <p className="mt-4 text-sm text-neutral-500 text-center">
                                            By submitting this form, you agree to our{' '}
                                            <a
                                                href="/tos"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-neutral-900 hover:underline"
                                                aria-label="Read our Terms of Service"
                                            >
                                                Terms of Service
                                            </a>
                                            {' '}and{' '}
                                            <a
                                                href="/privacy-policy"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-neutral-900 hover:underline"
                                                aria-label="Read our Privacy Policy"
                                            >
                                                Privacy Policy
                                            </a>.
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Navigation buttons */}
                    <div className="mt-8 flex items-center justify-between">
                        {currentStep > 0 ? (
                            <button
                                type="button"
                                onClick={goToPrev}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-950 hover:text-neutral-700 transition-colors"
                            >
                                <svg className="h-4 w-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                Back
                            </button>
                        ) : (
                            <div />
                        )}

                        {!isLastStep && (
                            <button
                                type="button"
                                onClick={goToNext}
                                className="inline-flex items-center gap-2 rounded-full bg-neutral-950 px-6 py-2.5 text-sm font-semibold text-white hover:bg-neutral-800 transition-colors"
                            >
                                Continue
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </FadeIn>
    )
}
